import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import {jsPDF} from 'jspdf';
import {autoTable} from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const CRMBudgets = () => {
  const [summary, setSummary] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [statusChartData, setStatusChartData] = useState([]);
  const [cityChartData, setCityChartData] = useState([]);
  const [chartDataValue, setChartDataValue] = useState([]);
  const [allBudgetsForExport, setAllBudgetsForExport] = useState([]);
  const [budgetChartMode, setBudgetChartMode] = useState('quantity');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBudgets, setTotalBudgets] = useState(0);
  
  // Estados para exportação e sincronização
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncDates, setSyncDates] = useState({
    startDate: '',
    endDate: ''
  });
  
  // Estado para ordenação
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  // Filtros expandidos
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    status: [],
    vendedor: '',
    search: ''
  });

  // Estados para dropdown multi-select
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  const exportMenuRef = useRef(null);

  // Fechar menu de exportação ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  // Fechar dropdown multi-select ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };
    if (statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownOpen]);

  // Função para obter cor do status consistente
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    switch(statusLower) {
      case 'aberto':
        return { bgColor: '#dbeafe', textColor: '#1e40af' };
      case 'enviado':
        return { bgColor: '#fef3c7', textColor: '#92400e' };
      case 'aprovado':
        return { bgColor: '#dcfce7', textColor: '#166534' };
      case 'recusado':
        return { bgColor: '#fee2e2', textColor: '#991b1b' };
      case 'expirado':
        return { bgColor: '#f3f4f6', textColor: '#374151' };
      case 'convertido':
        return { bgColor: '#ede9fe', textColor: '#5b21b6' };
      default:
        return { bgColor: '#f3f4f6', textColor: '#374151' };
    }
  };

  // Função para obter cor do tipo de orçamento
  const getTipoColor = (tipo) => {
    const tipoLower = tipo?.toLowerCase() || '';
    if (tipoLower.includes('venda')) {
      return { bgColor: '#d1fae5', textColor: '#065f46' };
    } else if (tipoLower.includes('compra')) {
      return { bgColor: '#fce7f3', textColor: '#831843' };
    } else if (tipoLower.includes('servi')) {
      return { bgColor: '#e0e7ff', textColor: '#3730a3' };
    }
    return { bgColor: '#f3f4f6', textColor: '#374151' };
  };

  // Função para formatar status para exibição
  const formatStatus = (status) => {
    if (!status) return 'Desconhecido';
    const statusLower = status.toLowerCase();
    if (statusLower === 'aberto') return 'Em Aberto';
    if (statusLower === 'enviado') return 'Enviado';
    if (statusLower === 'aprovado') return 'Aprovado';
    if (statusLower === 'recusado') return 'Recusado';
    if (statusLower === 'expirado') return 'Expirado';
    if (statusLower === 'convertido') return 'Convertido';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Função para ordenar dados
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
    loadData(1, { key, direction });
  };

  // Função para ordenar os dados da tabela
  const getSortedBudgets = () => {
    return budgets; // Ordenação feita no servidor
  };

  // Componente para cabeçalho ordenável
  const SortableHeader = ({ label, sortKey, style }) => (
    <th 
      onClick={() => handleSort(sortKey)}
      style={{ 
        ...style,
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative',
        transition: 'background-color 0.2s ease'
      }}
      onMouseOver={(e) => e.target.style.backgroundColor = '#f1f5f9'}
      onMouseOut={(e) => e.target.style.backgroundColor = '#f8fafc'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {label}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <svg 
            width="8" 
            height="4" 
            viewBox="0 0 8 4" 
            style={{ 
              fill: sortConfig.key === sortKey && sortConfig.direction === 'asc' ? '#22c55e' : '#d1d5db' 
            }}
          >
            <path d="M0 4L4 0L8 4H0Z"/>
          </svg>
          <svg 
            width="8" 
            height="4" 
            viewBox="0 0 8 4" 
            style={{ 
              fill: sortConfig.key === sortKey && sortConfig.direction === 'desc' ? '#22c55e' : '#d1d5db' 
            }}
          >
            <path d="M0 0L4 4L8 0H0Z"/>
          </svg>
        </div>
      </div>
    </th>
  );

  // Função para buscar TODOS os dados para os gráficos (sem paginação)
  const loadChartData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        return;
      }
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Buscar TODOS os dados sem paginação para os gráficos
      const chartParams = new URLSearchParams({ page_size: '9999' });
      if (filters.startDate) chartParams.append('startDate', filters.startDate);
      if (filters.endDate) chartParams.append('endDate', filters.endDate);
      if (filters.status && filters.status.length > 0) chartParams.append('status', filters.status.join(','));
      if (filters.vendedor) chartParams.append('vendedor', filters.vendedor);
      if (filters.search) chartParams.append('search', filters.search);

      console.log('Buscando dados completos para gráficos...');
      const chartResponse = await fetch(
        `/dashboard/api/crm/budgets/?${chartParams}`,
        { headers }
      );

      if (chartResponse.ok) {
        const chartData = await chartResponse.json();
        console.log('📊 ========== DADOS COMPLETOS DA API ==========');
        console.log('📊 Total de registros:', chartData.results?.length);
        console.log('📊 Primeiro registro completo:', JSON.stringify(chartData.results?.[0], null, 2));
        console.log('📊 Campos disponíveis no primeiro registro:', Object.keys(chartData.results?.[0] || {}));
        console.log('📊 ========================================');
        const allResults = chartData.results || [];
        setAllBudgetsForExport(allResults);
        processChartData(allResults);
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados do gráfico:', error);
    }
  };

  const loadData = async (page = 1, customSort = null) => {
    try {
      setLoading(true);

      const currentSort = customSort || sortConfig;

      const token = localStorage.getItem('access_token');

      if (!token) {
        console.error('Nenhum token encontrado no localStorage');
        alert('Sessão expirada. Faça login novamente.');
        window.location.href = '/login';
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Construir parâmetros de query (tratando arrays para multi-select)
      const buildParams = (baseParams = {}) => {
        const params = new URLSearchParams(baseParams);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.status && filters.status.length > 0) params.append('status', filters.status.join(','));
        if (filters.vendedor) params.append('vendedor', filters.vendedor);
        if (filters.search) params.append('search', filters.search);
        return params;
      };

      const summaryParams = buildParams();
      const listParams = buildParams({ page: page.toString(), page_size: '20' });
      if (currentSort.key) {
        listParams.append('sortBy', currentSort.key);
        listParams.append('sortDirection', currentSort.direction);
      }

      // Buscar dados para summary e tabela
      const [summaryResponse, budgetsResponse] = await Promise.all([
        fetch(`/dashboard/api/crm/budgets/summary/?${summaryParams}`, { headers }),
        fetch(`/dashboard/api/crm/budgets/?${listParams}`, { headers })
      ]);

      if (!summaryResponse.ok) {
        const errText = await summaryResponse.text().catch(() => '');
        console.error('Summary error:', summaryResponse.status, errText);
        throw new Error(`Erro ao buscar resumo: ${summaryResponse.status}`);
      }
      if (!budgetsResponse.ok) {
        const errText = await budgetsResponse.text().catch(() => '');
        console.error('Budgets error:', budgetsResponse.status, errText);
        throw new Error(`Erro ao buscar orçamentos: ${budgetsResponse.status}`);
      }

      const summaryData = await summaryResponse.json();
      const budgetsData = await budgetsResponse.json();
      
      console.log('📊 ========== SUMMARY (CARDS) ==========');
      console.log('📊 Summary completo:', JSON.stringify(summaryData, null, 2));
      console.log('📊 Campos do summary:');
      console.log('  • emandamento:', summaryData.emandamento);
      console.log('  • em_andamento:', summaryData.em_andamento);
      console.log('  • aprovado:', summaryData.aprovado);
      console.log('  • rejeitado:', summaryData.rejeitado);
      console.log('  • cancelado:', summaryData.cancelado);
      console.log('  • total:', summaryData.total);
      console.log('📊 ======================================');
      
      console.log('📋 ========== DADOS DA TABELA (PÁGINA', page, ') ==========');
      console.log('📋 Total de orçamentos:', budgetsData.count);
      if (budgetsData.results && budgetsData.results.length > 0) {
        const firstBudget = budgetsData.results[0];
        console.log('📋 Primeiro orçamento completo:', JSON.stringify(firstBudget, null, 2));
        console.log('📋 Campos críticos (snake_case da API):');
        console.log('  • budget_number:', firstBudget.budget_number);
        console.log('  • tipo_orcamento:', firstBudget.tipo_orcamento, '❌ NÃO EXISTE!');
        console.log('  • status:', firstBudget.status);
        console.log('  • total:', firstBudget.total);
        console.log('  • cliente_nome:', firstBudget.cliente_nome);
        console.log('  • cliente_cidade:', firstBudget.cliente_cidade, '❌ NÃO EXISTE!');
        console.log('  • data_emissao:', firstBudget.data_emissao);
      }
      console.log('📋 ========================================');
      
      setSummary(summaryData);
      setBudgets(budgetsData.results || []);
      setTotalPages(budgetsData.total_pages || 1);
      setTotalBudgets(budgetsData.count || 0);
      setCurrentPage(page);

      // Buscar dados completos para os gráficos (TODOS os registros, não apenas a página atual)
      loadChartData();
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      alert('Erro ao carregar dados: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (budgetsData) => {
    if (!budgetsData || budgetsData.length === 0) {
      setChartData([]);
      setStatusChartData([]);
      setCityChartData([]);
      return;
    }
    
    buildChartFromBudgets(budgetsData);
    buildStatusChart(budgetsData);
    buildCityChart(budgetsData);
  };

  const buildChartFromBudgets = (budgetsData) => {
    const monthlyData = {};
    const monthlyValueData = {};
    const monthNamesList = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

    const statusNames = ['Em Aberto', 'Enviado', 'Aprovado', 'Recusado', 'Expirado', 'Convertido'];
    const statusMap = {
      'aberto': 'Em Aberto',
      'enviado': 'Enviado',
      'aprovado': 'Aprovado',
      'recusado': 'Recusado',
      'expirado': 'Expirado',
      'convertido': 'Convertido'
    };

    budgetsData.forEach((budget) => {
      const date = new Date(budget.data_emissao || budget.dataEmissao);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = `${monthNamesList[date.getMonth()]}/${date.getFullYear()}`;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { month: monthName };
        monthlyValueData[monthKey] = { month: monthName };
        statusNames.forEach(s => {
          monthlyData[monthKey][s] = 0;
          monthlyValueData[monthKey][s] = 0;
        });
      }

      const statusLower = (budget.status || '').toLowerCase().trim();
      const chartStatus = statusMap[statusLower];

      if (chartStatus) {
        monthlyData[monthKey][chartStatus]++;
        monthlyValueData[monthKey][chartStatus] += parseFloat(budget.valor_total || budget.total || 0);
      }
    });

    const sortedKeys = Object.keys(monthlyData).sort();
    setChartData(sortedKeys.map(key => monthlyData[key]));
    setChartDataValue(sortedKeys.map(key => monthlyValueData[key]));
  };

  const buildStatusChart = (budgetsData) => {
    // Contar orçamentos por status
    const statusCount = {};
    let total = budgetsData.length;
    
    console.log('📊 Amostra de dados recebidos:', budgetsData.slice(0, 3));
    
    budgetsData.forEach((budget) => {
      const status = formatStatus(budget.status);
      statusCount[status] = (statusCount[status] || 0) + 1;
    });
    
    // Converter para array com porcentagens
    const chartData = Object.keys(statusCount).map(status => ({
      name: status,
      value: statusCount[status],
      percentage: total > 0 ? ((statusCount[status] / total) * 100).toFixed(1) : 0
    })).sort((a, b) => b.value - a.value);
    
    console.log('📊 Dados do gráfico de status:', chartData);
    setStatusChartData(chartData);
  };

  const buildCityChart = (budgetsData) => {
    // Contar orçamentos e somar valores por cidade
    const cityStats = {};

    budgetsData.forEach((budget) => {
      const cidade = budget.cliente_cidade || 'Não informado';

      if (!cityStats[cidade]) {
        cityStats[cidade] = {
          name: cidade,
          quantidade: 0,
          valor: 0
        };
      }

      cityStats[cidade].quantidade++;
      cityStats[cidade].valor += parseFloat(budget.valor_total || budget.total || 0);
    });

    // Converter para array e ordenar por quantidade (decrescente)
    const chartData = Object.values(cityStats)
      .sort((a, b) => b.quantidade - a.quantidade)
      .slice(0, 10) // Top 10 cidades
      .map(item => ({
        name: item.name.length > 20 ? item.name.substring(0, 20) + '...' : item.name,
        fullName: item.name,
        quantidade: item.quantidade,
        valor: parseFloat(item.valor.toFixed(2))
      }));

    setCityChartData(chartData);
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setCurrentPage(1);
  };

  const applyFilters = () => {
    loadData(1);
  };

  const validateSyncDates = () => {
    if (!syncDates.startDate || !syncDates.endDate) {
      alert('Data inicial e final são obrigatórias para sincronização');
      return false;
    }

    const startDate = new Date(syncDates.startDate);
    const endDate = new Date(syncDates.endDate);

    if (startDate > endDate) {
      alert('Data inicial deve ser anterior à data final');
      return false;
    }

    const diffDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
    if (diffDays > 365) {
      alert('Período máximo permitido: 365 dias');
      return false;
    }

    return true;
  };

  const handleSync = async () => {
    if (!validateSyncDates()) {
      return;
    }

    try {
      setSyncing(true);
      setShowSyncModal(false);
      
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        alert('Sessão expirada. Faça login novamente.');
        window.location.href = '/login';
        return;
      }
      
      const response = await fetch('/dashboard/api/crm/budgets/sync/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_date: syncDates.startDate,
          end_date: syncDates.endDate
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert(
          `Sincronização de orçamentos concluída com sucesso!\n\n` +
          `Estatísticas:\n` +
          `• ${result.total || 0} orçamentos processados\n` +
          `• ${result.created || 0} orçamentos criados\n` +
          `• ${result.updated || 0} orçamentos atualizados\n\n` +
          `Período: ${syncDates.startDate} a ${syncDates.endDate}`
        );
        
        const newFilters = {
          startDate: syncDates.startDate,
          endDate: syncDates.endDate,
          tipo_orcamento: '',
          status: '',
          search: ''
        };
        
        setFilters(newFilters);
        setCurrentPage(1);
        loadData(1);
      } else {
        alert('Erro na sincronização: ' + (result.message || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro na sincronização:', error);
      alert('Erro na sincronização: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const handlePageChange = (page) => {
    loadData(page);
  };

  const openSyncModal = () => {
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    setSyncDates({
      startDate,
      endDate
    });
    setShowSyncModal(true);
  };

  const formatCurrencyFull = (value) => {
    return `R$ ${parseFloat(value || 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatCurrencyShort = (value) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
    } else if (value >= 1000) {
      return `R$ ${(value / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}k`;
    }
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF('landscape');

      doc.setFontSize(18);
      doc.text('Relatório de Orçamentos CRM', 14, 20);

      doc.setFontSize(10);
      doc.text(`Período: ${filters.startDate} a ${filters.endDate}`, 14, 28);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 34);

      // Resumo por status
      doc.setFontSize(14);
      doc.text('Resumo por Status', 14, 45);

      const statusList = ['aberto', 'enviado', 'aprovado', 'recusado', 'expirado', 'convertido'];
      const summaryRows = statusList.map(st => ({
        label: formatStatus(st),
        count: summary?.[st] || 0,
        value: summary?.[st + '_value'] || 0
      })).sort((a, b) => b.value - a.value);
      const summaryBody = summaryRows.map(row => [
        row.label,
        row.count,
        formatCurrencyFull(row.value)
      ]);
      summaryBody.push(['TOTAL', summary?.total || 0, formatCurrencyFull(summary?.total_value || 0)]);

      autoTable(doc, {
        startY: 50,
        head: [['Status', 'Quantidade', 'Valor Total']],
        body: summaryBody,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [34, 197, 94] }
      });

      // Listagem
      doc.setFontSize(14);
      const finalY = doc.lastAutoTable.finalY || 50;
      doc.text('Listagem de Orçamentos', 14, finalY + 15);

      const statusOrder = { aberto: 1, enviado: 2, aprovado: 3, recusado: 4, expirado: 5, convertido: 6 };
      const sortedBudgets = [...allBudgetsForExport].sort((a, b) => {
        const statusA = statusOrder[a.status] || 99;
        const statusB = statusOrder[b.status] || 99;
        if (statusA !== statusB) return statusA - statusB;
        return parseFloat(b.valor_total || b.total || 0) - parseFloat(a.valor_total || a.total || 0);
      });

      const tableData = sortedBudgets.map(budget => [
        budget.budget_number || '-',
        (budget.cliente_nome || '-').substring(0, 35),
        formatStatus(budget.status),
        budget.data_emissao ? new Date(budget.data_emissao).toLocaleDateString('pt-BR') : '-',
        formatCurrencyFull(budget.valor_total || budget.total || 0),
        budget.vendedor_nome || '-',
        budget.condicao_pagamento || '-'
      ]);

      autoTable(doc, {
        startY: finalY + 20,
        head: [['Número', 'Cliente', 'Status', 'Data Emissão', 'Valor Total', 'Vendedor', 'Cond. Pagamento']],
        body: tableData,
        theme: 'striped',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [34, 197, 94] },
        margin: { left: 14, right: 14 }
      });

      const fileName = `Orcamentos_CRM_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      setShowExportMenu(false);
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      alert('Erro ao gerar PDF: ' + error.message);
    }
  };

  const exportToXLSX = () => {
    try {
      const statusOrder = { aberto: 1, enviado: 2, aprovado: 3, recusado: 4, expirado: 5, convertido: 6 };
      const sortedBudgets = [...allBudgetsForExport].sort((a, b) => {
        const statusA = statusOrder[a.status] || 99;
        const statusB = statusOrder[b.status] || 99;
        if (statusA !== statusB) return statusA - statusB;
        return parseFloat(b.valor_total || b.total || 0) - parseFloat(a.valor_total || a.total || 0);
      });

      const exportData = sortedBudgets.map(budget => ({
        'Número': budget.budget_number || '-',
        'Cliente': budget.cliente_nome || '-',
        'Status': formatStatus(budget.status),
        'Data Emissão': budget.data_emissao ? new Date(budget.data_emissao).toLocaleDateString('pt-BR') : '-',
        'Última Edição': budget.data_edicao ? new Date(budget.data_edicao).toLocaleDateString('pt-BR') : '-',
        'Valor Total': `R$ ${parseFloat(budget.valor_total || budget.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        'Vendedor': budget.vendedor_nome || '-',
        'Cond. Pagamento': budget.condicao_pagamento || '-',
        'Cidade': budget.cliente_cidade || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 10 }, { wch: 35 }, { wch: 12 }, { wch: 14 },
        { wch: 14 }, { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 20 }
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Orçamentos');

      // Aba de resumo
      const statusList = ['aberto', 'enviado', 'aprovado', 'recusado', 'expirado', 'convertido'];
      const summaryData = statusList.map(st => ({
        'Status': formatStatus(st),
        'Quantidade': summary?.[st] || 0,
        'Valor Total': `R$ ${parseFloat(summary?.[st + '_value'] || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }));
      summaryData.push({
        'Status': 'TOTAL',
        'Quantidade': summary?.total || 0,
        'Valor Total': `R$ ${parseFloat(summary?.total_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      });
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 15 }, { wch: 12 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumo');

      const fileName = `Orcamentos_CRM_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      setShowExportMenu(false);
    } catch (error) {
      console.error('Erro ao exportar XLSX:', error);
      alert('Erro ao exportar arquivo XLSX: ' + error.message);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading && budgets.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '400px',
        gap: '1rem'
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #e5e7eb', 
          borderTop: '4px solid #22c55e',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#6b7280', margin: 0 }}>Carregando orçamentos CRM...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header da Página */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{ color: '#1f2937', margin: 0, fontSize: '2rem', fontWeight: '700' }}>
            Orçamentos CRM
          </h1>
          <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
            Gestão completa com sincronização API M8 - Paginação automática implementada
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div ref={exportMenuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={allBudgetsForExport.length === 0}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: allBudgetsForExport.length === 0 ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: allBudgetsForExport.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exportar
            </button>
            {showExportMenu && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem',
                backgroundColor: 'white', borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                border: '1px solid #e5e7eb', zIndex: 1000, minWidth: '220px', overflow: 'hidden'
              }}>
                <button onClick={exportToXLSX} style={{
                  width: '100%', padding: '0.75rem 1rem', backgroundColor: 'transparent',
                  border: 'none', borderBottom: '1px solid #f1f5f9', textAlign: 'left',
                  cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <svg width="20" height="20" fill="#10b981" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>Exportar XLSX</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Planilha Excel com todos os dados</div>
                  </div>
                </button>
                <button onClick={exportToPDF} style={{
                  width: '100%', padding: '0.75rem 1rem', backgroundColor: 'transparent',
                  border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.875rem',
                  display: 'flex', alignItems: 'center', gap: '0.75rem', transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <svg width="20" height="20" fill="#dc2626" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>Exportar PDF</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Relatório PDF com resumo e listagem</div>
                  </div>
                </button>
              </div>
            )}
          </div>
          <button
            onClick={openSyncModal}
            disabled={syncing}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: syncing ? '#9ca3af' : '#22c55e',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: syncing ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 4px rgba(34, 197, 94, 0.3)'
            }}
          >
            {syncing ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ffffff40',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Sincronizando...
              </>
            ) : (
              <>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sincronizar Orçamentos
              </>
            )}
          </button>
        </div>
      </div>

      {/* Aviso sobre sincronização se não houver dados */}
      {!loading && budgets.length === 0 && (
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <svg width="24" height="24" fill="currentColor" style={{ color: '#0ea5e9', minWidth: '24px' }} viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0', fontWeight: '600', color: '#0c4a6e', fontSize: '1.125rem' }}>
              Nenhum orçamento encontrado
            </h3>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#0c4a6e', lineHeight: '1.5' }}>
              Configure as datas no botão "Sincronizar Orçamentos" para importar os dados da API M8. 
              O sistema processará automaticamente todas as páginas disponíveis.
            </p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        border: '1px solid #f1f5f9',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.25rem', fontWeight: '700' }}>
            Filtros de Visualização
          </h3>
        </div>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1.5rem',
          marginBottom: '2rem',
          alignItems: 'flex-end'
        }}>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Data Inicial
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange({ startDate: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#22c55e'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Data Final
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange({ endDate: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#22c55e'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          {/* Multi-select Status */}
          <div style={{ flex: '1 1 200px', minWidth: '200px', position: 'relative' }} ref={statusDropdownRef}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Status
            </label>
            <button
              type="button"
              onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${statusDropdownOpen ? '#22c55e' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none',
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left',
                color: filters.status.length === 0 ? '#6b7280' : '#1f2937'
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {filters.status.length === 0 ? 'Todos os Status' : `${filters.status.length} selecionado(s)`}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ minWidth: '12px', transform: statusDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                <path d="M2 4L6 8L10 4" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {statusDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 50,
                maxHeight: '250px',
                overflowY: 'auto'
              }}>
                {[
                  { value: 'aberto', label: 'Em Aberto' },
                  { value: 'enviado', label: 'Enviado' },
                  { value: 'aprovado', label: 'Aprovado' },
                  { value: 'recusado', label: 'Recusado' },
                  { value: 'expirado', label: 'Expirado' },
                  { value: 'convertido', label: 'Convertido' }
                ].map(opt => (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#374151',
                      borderBottom: '1px solid #f3f4f6',
                      backgroundColor: filters.status.includes(opt.value) ? '#f0fdf4' : 'white',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseOver={(e) => { if (!filters.status.includes(opt.value)) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = filters.status.includes(opt.value) ? '#f0fdf4' : 'white'; }}
                  >
                    <input
                      type="checkbox"
                      checked={filters.status.includes(opt.value)}
                      onChange={() => {
                        const newStatus = filters.status.includes(opt.value)
                          ? filters.status.filter(v => v !== opt.value)
                          : [...filters.status, opt.value];
                        handleFilterChange({ status: newStatus });
                      }}
                      style={{ accentColor: '#22c55e', width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Filtro por Vendedor */}
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Vendedor
            </label>
            <input
              type="text"
              placeholder="Nome do vendedor..."
              value={filters.vendedor}
              onChange={(e) => handleFilterChange({ vendedor: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#22c55e'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          {/* Busca geral */}
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Busca
            </label>
            <input
              type="text"
              placeholder="Número, cliente..."
              value={filters.search}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#22c55e'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          borderTop: '1px solid #f1f5f9',
          paddingTop: '1.5rem'
        }}>
          <button
            onClick={() => {
              setFilters({
                startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0],
                status: [],
                vendedor: '',
                search: ''
              });
              setStatusDropdownOpen(false);
              setCurrentPage(1);
              applyFilters();
            }}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'white',
              color: '#4b5563',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#9ca3af';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Limpar
          </button>

          <button
            onClick={applyFilters}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#22c55e',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#16a34a';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#22c55e';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Aplicar Filtros
          </button>
        </div>
      </div>

      {/* Cards de Resumo */}
      {summary && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {[
            { 
              label: 'Em Aberto', 
              count: summary.aberto || 0,  // ✅ Campo correto da API
              value: summary.aberto_value || 0,  // ✅ Campo correto da API
              color: '#3b82f6', 
              bgColor: '#dbeafe', 
              textColor: '#1e40af' 
            },
            { 
              label: 'Enviado', 
              count: summary.enviado || 0,  // ✅ Campo correto da API
              value: summary.enviado_value || 0,  // ✅ Campo correto da API
              color: '#f59e0b', 
              bgColor: '#fef3c7', 
              textColor: '#92400e' 
            },
            { 
              label: 'Aprovado', 
              count: summary.aprovado || 0, 
              value: summary.aprovado_value || 0,
              color: '#22c55e', 
              bgColor: '#dcfce7', 
              textColor: '#166534' 
            },
            { 
              label: 'Recusado', 
              count: summary.recusado || 0,  // ✅ recusado (não rejeitado!)
              value: summary.recusado_value || 0,  // ✅ recusado (não rejeitado!)
              color: '#dc2626', 
              bgColor: '#fee2e2', 
              textColor: '#991b1b' 
            },
            { 
              label: 'Expirado', 
              count: summary.expirado || 0,  // ✅ Campo correto da API
              value: summary.expirado_value || 0,  // ✅ Campo correto da API
              color: '#6b7280', 
              bgColor: '#f3f4f6', 
              textColor: '#374151' 
            },
            { 
              label: 'Convertido', 
              count: summary.convertido || 0,  // ✅ Campo correto da API
              value: summary.convertido_value || 0,  // ✅ Campo correto da API
              color: '#8b5cf6', 
              bgColor: '#ede9fe', 
              textColor: '#5b21b6' 
            },
            { 
              label: 'Total', 
              count: summary.total || 0, 
              value: summary.total_value || 0,
              color: '#6b7280', 
              bgColor: '#f3f4f6', 
              textColor: '#374151' 
            }
          ].map((item, index) => (
            <div key={index} style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
              border: '1px solid #f1f5f9',
              transition: 'transform 0.2s ease'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: item.textColor, fontSize: '0.875rem', fontWeight: '600' }}>{item.label}</h3>
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: item.color,
                  borderRadius: '50%'
                }}></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                  <p style={{ fontSize: '2.25rem', fontWeight: '800', margin: 0, color: '#1f2937', lineHeight: '1' }}>
                    {item.count}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>orçamentos</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: item.textColor }}>
                    R$ {parseFloat(item.value || 0).toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>valor total</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Gráfico de Status por Mês - com toggle quantidade/valor */}
      {chartData.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
          border: '1px solid #f1f5f9',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
              {budgetChartMode === 'quantity' ? 'Orçamentos por Mês e Status' : 'Valor dos Orçamentos por Mês e Status'}
            </h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setBudgetChartMode('quantity')}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  borderRadius: '6px',
                  border: budgetChartMode === 'quantity' ? 'none' : '1px solid #d1d5db',
                  backgroundColor: budgetChartMode === 'quantity' ? '#3b82f6' : 'white',
                  color: budgetChartMode === 'quantity' ? 'white' : '#4b5563',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Orçamentos
              </button>
              <button
                onClick={() => setBudgetChartMode('value')}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  borderRadius: '6px',
                  border: budgetChartMode === 'value' ? 'none' : '1px solid #d1d5db',
                  backgroundColor: budgetChartMode === 'value' ? '#10b981' : 'white',
                  color: budgetChartMode === 'value' ? 'white' : '#4b5563',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Valor dos Orçamentos
              </button>
            </div>
          </div>
          <div style={{ width: '100%', height: '400px' }}>
            <ResponsiveContainer>
              <BarChart
                data={budgetChartMode === 'quantity' ? chartData : chartDataValue}
                margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickLine={{ stroke: '#d1d5db' }}
                  domain={[0, 'dataMax + 5']}
                  tickFormatter={budgetChartMode === 'value' ? formatCurrencyShort : undefined}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                  labelStyle={{ color: '#374151', fontWeight: '500' }}
                  formatter={budgetChartMode === 'value' ? (value) => formatCurrencyFull(value) : undefined}
                />
                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                {[
                  { key: 'Em Aberto', color: '#3b82f6' },
                  { key: 'Enviado', color: '#f59e0b' },
                  { key: 'Aprovado', color: '#22c55e' },
                  { key: 'Recusado', color: '#dc2626' },
                  { key: 'Expirado', color: '#6b7280' },
                  { key: 'Convertido', color: '#8b5cf6' }
                ].map(bar => (
                  <Bar key={bar.key} dataKey={bar.key} fill={bar.color} name={bar.key} radius={[2, 2, 0, 0]}>
                    <LabelList dataKey={bar.key} position="top" formatter={(v) => v === 0 ? '' : (budgetChartMode === 'value' ? formatCurrencyShort(v) : v)} style={{ fill: '#1f2937', fontSize: '10px', fontWeight: '600' }} />
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Grid de Gráficos - Lado a Lado */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', 
        gap: '2rem',
        marginBottom: '2rem'
      }}>
        {/* Gráfico de Distribuição por Status */}
        {statusChartData.length > 0 && (
          <div style={{ 
            backgroundColor: 'white', 
            padding: '1.5rem', 
            borderRadius: '12px', 
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
            border: '1px solid #f1f5f9'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
              Distribuição por Status
            </h3>
            <div style={{ width: '100%', height: '350px' }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    outerRadius={100}
                    innerRadius={50}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusChartData.map((entry, index) => {
                      const statusColorMap = {
                        'Em Aberto': '#3b82f6',
                        'Enviado': '#f97316',
                        'Aprovado': '#22c55e',
                        'Recusado': '#dc2626',
                        'Expirado': '#6b7280',
                        'Convertido': '#f59e0b'
                      };
                      const fallbackColors = ['#3b82f6', '#22c55e', '#dc2626', '#f59e0b', '#6b7280', '#f97316'];
                      const color = statusColorMap[entry.name] || fallbackColors[index % fallbackColors.length];
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, 'Quantidade']}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value, entry) => `${value} (${entry.payload.percentage}%)`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ 
              textAlign: 'center', 
              marginTop: '1rem', 
              padding: '0.75rem', 
              backgroundColor: '#dbeafe', 
              borderRadius: '8px' 
            }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af', fontWeight: '500' }}>
                Total: {statusChartData.reduce((sum, item) => sum + item.value, 0)} orçamentos
              </p>
            </div>
          </div>
        )}

        {/* Gráfico de Ranking por Cidades */}
        {cityChartData.length > 0 && (
          <div style={{ 
            backgroundColor: 'white', 
            padding: '1.5rem', 
            borderRadius: '12px', 
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
            border: '1px solid #f1f5f9'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
              Ranking por Cidades - Top 10
            </h3>
            <div style={{ width: '100%', height: '350px', overflowY: 'auto' }}>
              {cityChartData.map((city, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  backgroundColor: index % 2 === 0 ? '#f8fafc' : 'white',
                  borderRadius: '6px',
                  border: '1px solid #f1f5f9',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateX(4px)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: index < 3 ? '#22c55e' : '#3b82f6',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: '700'
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                        {city.fullName}
                      </p>
                      <p style={{ margin: '0.125rem 0 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                        {city.quantidade} orçamento{city.quantidade !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: '#22c55e' }}>
                      R$ {city.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.75rem',
              marginTop: '1rem'
            }}>
              <div style={{ 
                textAlign: 'center', 
                padding: '0.75rem', 
                backgroundColor: '#dbeafe', 
                borderRadius: '8px' 
              }}>
                <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.625rem', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>
                  Total
                </p>
                <p style={{ margin: 0, fontSize: '1.25rem', color: '#1e40af', fontWeight: '700' }}>
                  {cityChartData.reduce((sum, item) => sum + item.quantidade, 0)}
                </p>
              </div>
              <div style={{ 
                textAlign: 'center', 
                padding: '0.75rem', 
                backgroundColor: '#d1fae5', 
                borderRadius: '8px' 
              }}>
                <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.625rem', color: '#065f46', fontWeight: '600', textTransform: 'uppercase' }}>
                  Valor Total
                </p>
                <p style={{ margin: 0, fontSize: '1.25rem', color: '#065f46', fontWeight: '700' }}>
                  R$ {cityChartData.reduce((sum, item) => sum + item.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Tabela de Orçamentos */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        border: '1px solid #f1f5f9',
        overflow: 'hidden'
      }}>
        <div style={{ 
          padding: '1.5rem', 
          borderBottom: '1px solid #f1f5f9', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#fafbfc'
        }}>
          <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.25rem', fontWeight: '700' }}>
            Listagem de Orçamentos
          </h2>
          <span style={{ 
            color: '#6b7280', 
            fontSize: '0.875rem',
            backgroundColor: '#f3f4f6',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontWeight: '500'
          }}>
            Total: {totalBudgets} orçamentos | Página {currentPage} de {totalPages}
          </span>
        </div>
        
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ 
              width: '32px', 
              height: '32px', 
              border: '3px solid #e5e7eb', 
              borderTop: '3px solid #22c55e',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }}></div>
            <p style={{ color: '#6b7280', margin: 0 }}>Carregando orçamentos...</p>
          </div>
        ) : budgets.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f8fafc' }}>
                  <tr>
                    <SortableHeader 
                      label="Número" 
                      sortKey="budget_number"
                      style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#4b5563', 
                        borderBottom: '2px solid #e5e7eb' 
                      }}
                    />
                    <SortableHeader 
                      label="Cliente" 
                      sortKey="cliente_nome"
                      style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#4b5563', 
                        borderBottom: '2px solid #e5e7eb' 
                      }}
                    />
                    <SortableHeader 
                      label="Status" 
                      sortKey="status"
                      style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#4b5563', 
                        borderBottom: '2px solid #e5e7eb' 
                      }}
                    />
                    <SortableHeader 
                      label="Data Emissão" 
                      sortKey="data_emissao"
                      style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#4b5563', 
                        borderBottom: '2px solid #e5e7eb' 
                      }}
                    />
                    <SortableHeader
                      label="Última Edição"
                      sortKey="data_edicao"
                      style={{
                        padding: '1rem',
                        textAlign: 'left',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#4b5563',
                        borderBottom: '2px solid #e5e7eb'
                      }}
                    />
                    <SortableHeader
                      label="Valor Total"
                      sortKey="valor_total"
                      style={{
                        padding: '1rem',
                        textAlign: 'left',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#4b5563',
                        borderBottom: '2px solid #e5e7eb'
                      }}
                    />
                  </tr>
                </thead>
                <tbody>
                  {getSortedBudgets().map((budget) => {
                    const statusColors = getStatusColor(budget.status);
                    return (
                      <tr key={budget.id} style={{ 
                        borderBottom: '1px solid #f1f5f9',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>
                          {budget.budget_number || budget.numeroProposta || budget.numero_proposta || '-'}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {budget.cliente_nome || budget.clienteNome || budget.clienteRazaoSocial || budget.cliente_razao || '-'}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                          <span style={{
                            padding: '0.375rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: statusColors.bgColor,
                            color: statusColors.textColor
                          }}>
                            {formatStatus(budget.status)}
                          </span>
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {new Date(budget.data_emissao || budget.dataEmissao).toLocaleDateString('pt-BR')}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {budget.data_edicao ? new Date(budget.data_edicao).toLocaleDateString('pt-BR') : '-'}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                          R$ {parseFloat(budget.total || budget.valor_total || 0).toLocaleString('pt-BR', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div style={{ 
                padding: '1.5rem', 
                borderTop: '1px solid #f1f5f9', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: '0.75rem',
                backgroundColor: '#fafbfc'
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.75rem 1rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (currentPage !== 1) {
                      e.target.style.borderColor = '#22c55e';
                      e.target.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = 'white';
                  }}
                >
                  ← Anterior
                </button>

                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={i}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        padding: '0.75rem 1rem',
                        border: `2px solid ${pageNum === currentPage ? '#22c55e' : '#e5e7eb'}`,
                        backgroundColor: pageNum === currentPage ? '#22c55e' : 'white',
                        color: pageNum === currentPage ? 'white' : '#374151',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '0.875rem',
                        minWidth: '44px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        if (pageNum !== currentPage) {
                          e.target.style.borderColor = '#22c55e';
                          e.target.style.backgroundColor = '#f8fafc';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (pageNum !== currentPage) {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.75rem 1rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (currentPage !== totalPages) {
                      e.target.style.borderColor = '#22c55e';
                      e.target.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = 'white';
                  }}
                >
                  Próxima →
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: '4rem', textAlign: 'center', color: '#6b7280' }}>
            <svg style={{ 
              width: '64px', 
              height: '64px', 
              margin: '0 auto 1.5rem', 
              color: '#d1d5db' 
            }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
              Nenhum orçamento encontrado
            </h3>
            <p style={{ margin: 0, lineHeight: '1.6' }}>
              Ajuste os filtros ou clique em "Sincronizar Orçamentos" para importar dados da API M8.
            </p>
          </div>
        )}
      </div>

      {/* Modal de Sincronização */}
      {showSyncModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '16px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            width: '90%',
            maxWidth: '500px',
            border: '1px solid #f1f5f9'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{
                padding: '0.5rem',
                backgroundColor: '#dcfce7',
                borderRadius: '8px'
              }}>
                <svg width="24" height="24" fill="none" stroke="currentColor" style={{ color: '#22c55e' }} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '700' }}>
                Configurar Sincronização de Orçamentos
              </h3>
            </div>
            
            <div style={{ 
              backgroundColor: '#f0f9ff', 
              border: '1px solid #7dd3fc', 
              borderRadius: '10px', 
              padding: '1rem', 
              marginBottom: '1.5rem' 
            }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#0369a1', lineHeight: '1.5' }}>
                <strong>Paginação Inteligente:</strong> O sistema irá buscar TODOS os orçamentos do período especificado, 
                processando automaticamente todas as páginas da API M8.
              </p>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                color: '#374151'
              }}>
                Data Inicial: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="date"
                value={syncDates.startDate}
                onChange={(e) => setSyncDates({...syncDates, startDate: e.target.value})}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `2px solid ${!syncDates.startDate ? '#ef4444' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#22c55e'}
                onBlur={(e) => e.target.style.borderColor = !syncDates.startDate ? '#ef4444' : '#e5e7eb'}
              />
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                color: '#374151'
              }}>
                Data Final: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="date"
                value={syncDates.endDate}
                onChange={(e) => setSyncDates({...syncDates, endDate: e.target.value})}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `2px solid ${!syncDates.endDate ? '#ef4444' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#22c55e'}
                onBlur={(e) => e.target.style.borderColor = !syncDates.endDate ? '#ef4444' : '#e5e7eb'}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSyncModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = 'white';
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSync}
                disabled={!syncDates.startDate || !syncDates.endDate}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: (!syncDates.startDate || !syncDates.endDate) ? '#9ca3af' : '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!syncDates.startDate || !syncDates.endDate) ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  transition: 'background-color 0.2s ease',
                  boxShadow: (!syncDates.startDate || !syncDates.endDate) ? 'none' : '0 2px 4px rgba(34, 197, 94, 0.3)'
                }}
                onMouseOver={(e) => {
                  if (syncDates.startDate && syncDates.endDate) {
                    e.target.style.backgroundColor = '#16a34a';
                  }
                }}
                onMouseOut={(e) => {
                  if (syncDates.startDate && syncDates.endDate) {
                    e.target.style.backgroundColor = '#22c55e';
                  }
                }}
              >
                Sincronizar Orçamentos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMBudgets;